import type { ReactNode } from "react";
import { Box, Paper, Typography } from "@mui/material";
import type { NsIntegration, NsMap, NsPeriod } from "@/api/netsuiteMap";
import { MONO, isNew } from "./shared";

// What changed, and when. A period's count is the integrations whose since
// falls in [from, until); pre: true also takes the ones dated "pre".
export const inPeriod = (p: NsPeriod) => (i: NsIntegration) =>
  i.since === "pre" ? !!p.pre : (!p.from || i.since >= p.from) && i.since < p.until;

function Column({ title, meta, current, children }: { title: string; meta: string; current?: boolean; children: ReactNode }) {
  return (
    <Box sx={{ p: 2, borderLeft: 1, borderTop: 1, borderColor: "divider", display: "grid", alignContent: "start", gap: 1, minWidth: 0, ml: "-1px", mt: "-1px" }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1, color: current ? "secondary.main" : "text.primary" }}>
        {title}
        <Box component="span" sx={{ fontFamily: MONO, fontSize: 11, color: "text.secondary", fontWeight: 400 }}>{meta}</Box>
      </Typography>
      {children}
    </Box>
  );
}

function Bullets({ items }: { items: { text: string; highlight?: boolean }[] }) {
  return (
    <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gap: 0.9, fontSize: 13.5, lineHeight: 1.4 }}>
      {items.map((it) => (
        <Box
          component="li"
          key={it.text}
          sx={{
            pl: 1.5, position: "relative", fontWeight: it.highlight ? 600 : 400,
            "&::before": { content: "\"\"", position: "absolute", left: 0, top: ".55em", width: 5, height: 5, borderRadius: "1px", bgcolor: it.highlight ? "secondary.main" : "text.secondary" },
          }}
        >
          {it.text}
        </Box>
      ))}
    </Box>
  );
}

export function Timeline({ map }: { map: NsMap }) {
  const { integrations } = map;
  const counts = map.timeline.map((p) => integrations.filter(inPeriod(p)).length);
  const max = Math.max(1, ...counts);
  const fresh = integrations.filter(isNew).length;
  const latest = map.timeline.find((p) => p.current);
  const latestCount = latest ? integrations.filter(inPeriod(latest)).length : 0;

  return (
    <Paper variant="outlined" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0,1fr))", lg: "repeat(4, minmax(0,1fr))" }, overflow: "hidden" }}>
      {map.timeline.map((p, i) => (
        <Column key={p.label} title={p.label} meta={`${counts[i]} pts`} current={p.current}>
          <Box sx={{ height: 6, bgcolor: "action.hover", borderRadius: 3, overflow: "hidden" }} aria-hidden>
            <Box sx={{ height: "100%", width: `${Math.round((counts[i] / max) * 100)}%`, bgcolor: "secondary.main" }} />
          </Box>
          <Bullets items={p.items} />
        </Column>
      ))}
      <Column title="Next" meta="planned">
        <Bullets items={map.timeline_next.map((text) => ({ text }))} />
      </Column>
      <Column title="Pace" meta="since May">
        <Typography variant="body2" color="text.secondary">
          {fresh} of the {integrations.length} integration points on this page shipped in the last six months.
          {latest && ` ${latestCount} of them shipped in ${latest.label} alone, which is why the improvement is not yet visible in a closed month.`}
        </Typography>
      </Column>
    </Paper>
  );
}
